import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// On a 401, transparently try the refresh-token cookie once, then retry the
// original request. A single in-flight refresh is shared across concurrent 401s.
// The refresh/login endpoints themselves are excluded to avoid loops.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const url = original.url || "";
    // Skip refresh for auth endpoints themselves (google/request-link/verify-link/
    // refresh/logout) to avoid loops — but /auth/me MUST be allowed to trigger a
    // refresh so sessions restore on reload.
    const skip = url.includes("/auth/refresh") || (url.includes("/auth/") && !url.includes("/auth/me"));
    if (status === 401 && !original._retry && !skip) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return String(d);
}

export function fmtMoney(amount, currency = "INR") {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const sym = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency + " ";
  return `${sym}${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export const QUOTE_STATUS = {
  draft: { label: "Draft", cls: "pill-mute" },
  submitted: { label: "Submitted", cls: "pill-mute" },
  under_review: { label: "Under Review", cls: "pill-warn" },
  more_info_required: { label: "More Info Needed", cls: "pill-warn" },
  negotiating: { label: "Negotiating", cls: "pill-warn" },
  quotation_prepared: { label: "Quotation Prepared", cls: "pill-gold" },
  awaiting_seller_approval: { label: "Awaiting Your Approval", cls: "pill-gold" },
  awaiting_payment: { label: "Awaiting Payment", cls: "pill-gold" },
  paid: { label: "Paid", cls: "pill-ok" },
  converted_to_job: { label: "In Production", cls: "pill-ok" },
  expired: { label: "Expired", cls: "pill-mute" },
  cancelled: { label: "Cancelled", cls: "pill-danger" },
  refunded: { label: "Refunded", cls: "pill-danger" },
};

export const JOB_STATUS = {
  preparing: { label: "Preparing", cls: "pill-mute" },
  available: { label: "Available", cls: "pill-gold" },
  claimed: { label: "Claimed", cls: "pill-warn" },
  in_progress: { label: "In Progress", cls: "pill-warn" },
  work_submitted: { label: "Work Submitted", cls: "pill-warn" },
  awaiting_seller_review: { label: "Awaiting Your Review", cls: "pill-gold" },
  revision_requested: { label: "Revision Requested", cls: "pill-warn" },
  revision_in_progress: { label: "Revision In Progress", cls: "pill-warn" },
  resubmitted: { label: "Resubmitted", cls: "pill-warn" },
  approved: { label: "Approved", cls: "pill-ok" },
  completed: { label: "Completed", cls: "pill-ok" },
  disputed: { label: "Disputed", cls: "pill-danger" },
  cancelled: { label: "Cancelled", cls: "pill-danger" },
  refunded: { label: "Refunded", cls: "pill-danger" },
};

export const FREELANCER_STATUS = {
  draft: { label: "Draft", cls: "pill-mute" },
  submitted: { label: "Submitted", cls: "pill-warn" },
  under_review: { label: "Under Review", cls: "pill-warn" },
  approved: { label: "Approved", cls: "pill-ok" },
  rejected: { label: "Rejected", cls: "pill-danger" },
  suspended: { label: "Suspended", cls: "pill-danger" },
};
